import {
  Alert,
  AlertIcon,
  AlertDescription,
  HStack,
  Input,
  InputGroup,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import {
  ClassedDrawRequests,
  ClassedDrawRequestsForMultiClass,
} from './ClassedDrawRequestAmount';
import {
  canDrawRequestBeSubmitted,
  canRecycleRequestBeSubmitted,
  getFailedConstraintsMessages,
} from './GlobalConstraints';
import {
  useCanSubmitDrawRequestAlphaCustomers,
  useCanSubmitRecycleRequestAlphaCustomers,
} from './hooks';
import { LoanRequests } from './LoanRequests';
import { PricingOptionSelect } from './PricingOptionSelect';
import { RecycleRequests } from './RecycleRequests';
import { SharedInputLabel, sharedSectionStyles } from './Shared';
import { FundingRequestTypeEnumGQLType } from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { useFundingRequestTemplateContextV1 } from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';

type CreateRequestProps = {
  type: FundingRequestTypeEnumGQLType;
};

export const CreateRequest = ({ type }: CreateRequestProps) => {
  const { context } = useFundingRequestTemplateContextV1();
  // @VLYSLS @jendevelops conditionally hide borrowing base dependent values if no cfs w/ borrowing base
  const { isSlsAgentLender } = useFeatureFlags();

  const {
    allowedActions,
    data: {
      drawRequests,
      maturityDate: initialMaturityDate,
      recycleRequests,
      expectedSubmission,
      externalLoanId: initialExternalLoanId,
    },
    setValue,
  } = context;

  const [targetFundingDate, setTargetFundingDate] = useState(
    expectedSubmission.draw.global.targetFundingDate,
  );

  const [targetRecycleDate, setTargetRecycleDate] = useState(
    expectedSubmission.recycle.global.targetFundingDate,
  );

  const [maturityDate, setMaturityDate] = useState(initialMaturityDate);

  const [externalLoanId, setExternalLoanId] = useState(initialExternalLoanId);

  useEffect(() => {
    setExternalLoanId(initialExternalLoanId);
  }, [initialExternalLoanId]);

  useEffect(() => {
    setTargetRecycleDate(expectedSubmission.recycle.global.targetFundingDate);
  }, [expectedSubmission]);

  useEffect(() => {
    setTargetFundingDate(expectedSubmission.draw.global.targetFundingDate);
  }, [expectedSubmission]);

  useEffect(() => {
    setMaturityDate(initialMaturityDate);
  }, [initialMaturityDate]);

  const handleSetExternalLoanId = useCallback(() => {
    setValue({
      type: 'setExternalLoanId',
      options: {
        externalLoanId: externalLoanId || undefined,
      },
    });
  }, [externalLoanId, setValue]);

  const recycleRequestCount = recycleRequests.length;

  const hasDrawRequest = drawRequests.length > 0;

  return (
    <>
      {/* @jendevelops TODO - only show if has loan mechanics */}
      {isSlsAgentLender && (
        <HStack {...sharedSectionStyles}>
          <SharedInputLabel label="External Loan ID" />
          <VStack spacing="8px" alignItems="flex-start" p="none" w="100%">
            <InputGroup w="100%">
              <Input
                w="100%"
                type="text"
                bgColor="white"
                isDisabled={
                  allowedActions === 'readOnly' || type === 'upsizing'
                }
                onChange={(evt) => {
                  setExternalLoanId(evt.target.value);
                }}
                onBlur={handleSetExternalLoanId}
                value={externalLoanId}
              />
            </InputGroup>
            <Typography variant="detail-muted">Optional</Typography>
          </VStack>
        </HStack>
      )}
      {/* @jendevelops TODO - derive visibility from loan mechanics/ pricing option
      is an array with at least one option */}
      {isSlsAgentLender && (
        <HStack {...sharedSectionStyles}>
          <SharedInputLabel label="Pricing Option" />
          <PricingOptionSelect isDisabled={type === 'upsizing'} />
        </HStack>
      )}
      {!!recycleRequestCount && type !== 'upsizing' && (
        <>
          <HStack {...sharedSectionStyles}>
            <SharedInputLabel label="Recycle Request Amount" />
            <RecycleRequests />
          </HStack>
          <HStack {...sharedSectionStyles}>
            <SharedInputLabel label="Recycle Date" />
            <SimpleDatePicker
              dateFormat="MMMM dd, yyyy"
              disabled={context.allowedActions === 'readOnly'}
              selectedDate={
                targetRecycleDate
                  ? commonDate.fullDateStrToDate(targetRecycleDate)
                  : null
              } // @jendevelops default to max?
              setSelectedDate={(date: Date | null) => {
                if (!date) return;
                const newDate = commonDate.dateToFullDateString(date);
                setTargetRecycleDate(newDate);
                // happens on change vs on blur because once you select a date, it doesn't unfocus unless you click out of the input
                setValue({
                  type: 'setTargetFundingDate',
                  options: {
                    option: 'recycle',
                    // @jendevelops TODO: pipe in is global to use key or not to be able to set individual recycle request
                    targetFundingDate: newDate,
                  },
                });
              }}
              width="100%"
            />
          </HStack>
        </>
      )}
      {hasDrawRequest && (
        <>
          <HStack {...sharedSectionStyles}>
            <SharedInputLabel label="Draw Request Amount" />
            <ClassedDrawRequests />
          </HStack>
          <HStack {...sharedSectionStyles}>
            <SharedInputLabel label="Funding Date" />
            <SimpleDatePicker
              dateFormat="MMMM dd, yyyy"
              excludeHolidays
              excludeWeekends
              disabled={context.allowedActions === 'readOnly'}
              selectedDate={
                targetFundingDate
                  ? commonDate.fullDateStrToDate(targetFundingDate)
                  : null
              }
              setSelectedDate={(date: Date | null) => {
                if (!date) return;
                const newDate = commonDate.dateToFullDateString(date);
                setTargetFundingDate(newDate);
                // happens on change vs on blur because once you select a date, it doesn't unfocus unless you click out of the input
                setValue({
                  type: 'setTargetFundingDate',
                  options: {
                    option: 'draw',
                    // @jendevelops TODO: pipe in is global to use key or not to be able to set individual recycle request
                    targetFundingDate: newDate,
                  },
                });
              }}
              width="100%"
            />
          </HStack>
        </>
      )}
      {/* @jendevelops TODO - only show maturity date if has loan mechanics */}
      {isSlsAgentLender && (
        <HStack {...sharedSectionStyles}>
          <SharedInputLabel label="Maturity Date" />
          <SimpleDatePicker
            dateFormat="MMMM dd, yyyy"
            width="100%"
            disabled={
              context.allowedActions === 'readOnly' || type === 'upsizing'
            }
            selectedDate={
              maturityDate ? commonDate.fullDateStrToDate(maturityDate) : null
            } // @jendevelops default to max?
            setSelectedDate={(date: Date | null) => {
              if (!date) return;
              const newDate = commonDate.dateToFullDateString(date);
              setMaturityDate(newDate);
              // happens on change vs on blur because once you select a date, it doesn't unfocus unless you click out of the input
              setValue({
                type: 'setMaturityDate',
                options: {
                  date: newDate,
                },
              });
            }}
          />
        </HStack>
      )}
      {/* @jendevelops TODO: how do we determine this value?  */}
      {/* <HStack {...sharedSectionStyles}>
        <Text {...sharedInputLabelStyles}>Payment Schedule</Text>
        <InputGroup flexGrow={1} bgColor="white">
          <InputLeftElement pointerEvents="none">
            <Icon icon="calendar" />
          </InputLeftElement>
          <Input
            type="date"
            value={undefined} // @jendevelops default to max?
            isDisabled
            _invalid={{
              border: `1px solid ${colors.red[400]}`,
              boxShadow: 'none',
            }}
          />
        </InputGroup>
      </HStack> */}
      {/* <HStack {...sharedSectionStyles}>
        <Text {...sharedInputLabelStyles}>Invoice Lead Time</Text>
        <HStack w="100%" spacing="8px">
          <Text>Send </Text>
          <InputGroup w="100px" bgColor="white">
            <Select
              placeholderLabel="Select pricing option"
              // @jendevelops TODO: are these options going to be determined by CA, or just have a set of options for all customers. is default defined in CA?
              options={[
                {
                  label: '3',
                  value: 3,
                },
                {
                  label: '5',
                  value: 5,
                },
                {
                  label: '7',
                  value: 7,
                },
              ]}
              // @jendevelops TODO: hook up state
              value={3}
              onChange={() => {}}
            />
          </InputGroup>
          <Text> days ahead of time</Text>
        </HStack>
      </HStack> */}
      {/* @jendevelops TODO hook up state */}
      {/* <HStack {...sharedSectionStyles}>
        <Text {...sharedInputLabelStyles} alignSelf="flex-start">
          Memo
        </Text>
        <RichTextEditor
          text=""
          setText={() => {}}
          isEditable
          placeHolderText="Leave a comment"
          width="100%"
        />
      </HStack> */}
    </>
  );
};

export const CreateMultiLoanRequest = ({ type }: CreateRequestProps) => {
  const { context } = useFundingRequestTemplateContextV1();
  // @VLYSLS @jendevelops conditionally hide borrowing base dependent values if no cfs w/ borrowing base
  const { isSlsAgentLender } = useFeatureFlags();

  const {
    data: { drawRequests, recycleRequests, expectedSubmission },
    setValue,
  } = context;

  const [targetRecycleDate, setTargetRecycleDate] = useState(
    expectedSubmission.recycle.global.targetFundingDate,
  );

  useEffect(() => {
    setTargetRecycleDate(expectedSubmission.recycle.global.targetFundingDate);
  }, [expectedSubmission]);

  const recycleRequestCount = recycleRequests.length;

  const hasDrawRequest = drawRequests.length > 0;

  return (
    <>
      {isSlsAgentLender && (
        <HStack {...sharedSectionStyles}>
          <SharedInputLabel label="Pricing Option" />
          <PricingOptionSelect isDisabled={type === 'upsizing'} />
        </HStack>
      )}
      {!!recycleRequestCount && type !== 'upsizing' && (
        <>
          <HStack {...sharedSectionStyles}>
            <SharedInputLabel label="Recycle Request Amount" />
            <RecycleRequests />
          </HStack>
          <HStack {...sharedSectionStyles}>
            <SharedInputLabel label="Recycle Date" />
            <SimpleDatePicker
              dateFormat="MMMM dd, yyyy"
              disabled={context.allowedActions === 'readOnly'}
              selectedDate={
                targetRecycleDate
                  ? commonDate.fullDateStrToDate(targetRecycleDate)
                  : null
              } // @jendevelops default to max?
              setSelectedDate={(date: Date | null) => {
                if (!date) return;
                const newDate = commonDate.dateToFullDateString(date);
                setTargetRecycleDate(newDate);
                // happens on change vs on blur because once you select a date, it doesn't unfocus unless you click out of the input
                setValue({
                  type: 'setTargetFundingDate',
                  options: {
                    option: 'recycle',
                    // @jendevelops TODO: pipe in is global to use key or not to be able to set individual recycle request
                    targetFundingDate: newDate,
                  },
                });
              }}
              width="100%"
            />
          </HStack>
        </>
      )}
      {hasDrawRequest && <LoanRequests type={type} />}
    </>
  );
};

/**
 * THIS IS LIVE IN PROD DO NOT UPDATE BEFORE TALKING TO @jendevelops
 */
export const CreateRequestForMultiClass = () => {
  const { context } = useFundingRequestTemplateContextV1();
  const {
    canUserOrgSubmitDrawRequest,
  } = useCanSubmitDrawRequestAlphaCustomers();
  const {
    canUserOrgSubmitRecycleRequest,
  } = useCanSubmitRecycleRequestAlphaCustomers();

  const {
    allowedActions,
    data: { drawRequests, recycleRequests, expectedSubmission },
    setValue,
  } = context;

  const [targetFundingDate, setTargetFundingDate] = useState(
    expectedSubmission.draw.global.targetFundingDate,
  );

  const [targetRecycleDate, setTargetRecycleDate] = useState(
    expectedSubmission.recycle.global.targetFundingDate,
  );

  useEffect(() => {
    setTargetRecycleDate(expectedSubmission.recycle.global.targetFundingDate);
  }, [expectedSubmission]);

  useEffect(() => {
    setTargetFundingDate(expectedSubmission.draw.global.targetFundingDate);
  }, [expectedSubmission]);

  const recycleRequestCount = recycleRequests.length;

  const hasDrawRequest = drawRequests.length > 0;

  const canDrawBeSubmitted =
    allowedActions === 'mutable' &&
    canUserOrgSubmitDrawRequest &&
    canDrawRequestBeSubmitted(expectedSubmission.draw);

  const globalDrawFailedConstraints = getFailedConstraintsMessages(
    expectedSubmission.draw.global.constraints,
  );

  const canRecycleBeSubmitted =
    allowedActions === 'mutable' &&
    canUserOrgSubmitRecycleRequest &&
    canRecycleRequestBeSubmitted(expectedSubmission.recycle);

  const globalRecycleFailedConstraints = getFailedConstraintsMessages(
    expectedSubmission.recycle.global.constraints,
  );

  return (
    <>
      {!!recycleRequestCount &&
        !canRecycleBeSubmitted &&
        globalRecycleFailedConstraints.length > 0 && (
          <>
            {globalRecycleFailedConstraints.map((constraint) => (
              <Alert status="error" key={constraint}>
                <AlertIcon />
                <AlertDescription>{`Failed ${constraint}`}</AlertDescription>
              </Alert>
            ))}
          </>
        )}
      {!!recycleRequestCount && (
        <>
          <HStack {...sharedSectionStyles}>
            <SharedInputLabel label="Recycle Request Amount" />
            <RecycleRequests isDisabled={!canRecycleBeSubmitted} />
          </HStack>
          <HStack {...sharedSectionStyles}>
            <SharedInputLabel label="Recycle Date" />
            <SimpleDatePicker
              disabled={allowedActions === 'readOnly' || !canRecycleBeSubmitted}
              selectedDate={
                targetRecycleDate
                  ? commonDate.fullDateStrToDate(targetRecycleDate)
                  : null
              }
              setSelectedDate={(date: Date | null) => {
                if (!date) return;
                const newDate = commonDate.dateToFullDateString(date);
                setTargetRecycleDate(newDate);
                // happens on change vs on blur because once you select a date, it doesn't unfocus unless you click out of the input
                setValue({
                  type: 'setTargetFundingDate',
                  options: {
                    option: 'recycle',
                    targetFundingDate: newDate,
                  },
                });
              }}
              width="100%"
            />
          </HStack>
        </>
      )}
      {hasDrawRequest &&
        !canDrawBeSubmitted &&
        globalDrawFailedConstraints.length > 0 && (
          <>
            {globalDrawFailedConstraints.map((constraint) => (
              <Alert status="error" key={constraint}>
                <AlertIcon />
                <AlertDescription>{`Failed ${constraint}`}</AlertDescription>
              </Alert>
            ))}
          </>
        )}
      {hasDrawRequest && (
        <>
          <HStack {...sharedSectionStyles}>
            <SharedInputLabel label="Draw Request Amount" />
            <ClassedDrawRequestsForMultiClass />
          </HStack>
          <HStack {...sharedSectionStyles}>
            <SharedInputLabel label="Funding Date" />
            <SimpleDatePicker
              disabled={allowedActions === 'readOnly' || !canDrawBeSubmitted}
              excludeHolidays
              excludeWeekends
              selectedDate={
                targetFundingDate
                  ? commonDate.fullDateStrToDate(targetFundingDate)
                  : null
              } // @jendevelops default to max?
              setSelectedDate={(date: Date | null) => {
                if (!date) return;
                const newDate = commonDate.dateToFullDateString(date);
                setTargetFundingDate(newDate);
                // happens on change vs on blur because once you select a date, it doesn't unfocus unless you click out of the input
                setValue({
                  type: 'setTargetFundingDate',
                  options: {
                    option: 'draw',
                    targetFundingDate: newDate,
                  },
                });
              }}
              width="100%"
            />
          </HStack>
        </>
      )}
    </>
  );
};
